import { useEffect, useRef, useState } from "react";
import { useCan, useInvalidate, useOne } from "@refinedev/core";
import { useSession } from "hooks/useSession";
import type { BundleResponse, DataExportStatus } from "@/types";
import { exportsResource, useDataExports } from "./useDataExports";

const POLLING_INTERVAL_MS = 10_000;
const FINAL_STATUSES = new Set<DataExportStatus>(["Completed", "Failed"]);

const useDocumentVisible = () => {
  const [visible, setVisible] = useState(
    typeof document === "undefined"
      ? true
      : document.visibilityState === "visible"
  );

  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  return visible;
};

type ActiveExportOptions = {
  onTerminalTransition?: (bundle: BundleResponse) => void;
};

export const useActiveExport = ({
  onTerminalTransition,
}: ActiveExportOptions = {}) => {
  const { currentOrganization } = useSession();
  const orgId = currentOrganization?.id;
  const resource = orgId ? exportsResource(orgId) : "";

  const { data: canAccess } = useCan({
    resource: "organization",
    action: "edit",
  });
  const isAdmin = canAccess?.can ?? false;

  const visible = useDocumentVisible();
  const invalidate = useInvalidate();

  const { activeBundle: candidateFromList } = useDataExports();
  const requestId = candidateFromList?.id;

  const lastStatusRef = useRef<DataExportStatus | undefined>(
    candidateFromList?.status
  );
  const firedTransitionForRef = useRef<string | undefined>(undefined);

  const detail = useOne<BundleResponse>({
    resource,
    id: requestId ?? "",
    queryOptions: {
      enabled: isAdmin && Boolean(orgId) && Boolean(requestId) && visible,
      refetchInterval: (data) => {
        const status = data?.data.status;
        if (!status) return POLLING_INTERVAL_MS;
        return FINAL_STATUSES.has(status) ? false : POLLING_INTERVAL_MS;
      },
    },
  });

  const liveBundle = detail.data?.data ?? candidateFromList;

  useEffect(() => {
    const status = liveBundle?.status;
    const id = liveBundle?.id;
    if (!status || !id) return;

    const previous = lastStatusRef.current;
    lastStatusRef.current = status;

    const transitioned =
      FINAL_STATUSES.has(status) &&
      previous !== undefined &&
      !FINAL_STATUSES.has(previous);

    if (transitioned && firedTransitionForRef.current !== id) {
      firedTransitionForRef.current = id;
      invalidate({ resource, invalidates: ["list"] });
      onTerminalTransition?.(liveBundle);
    }
  }, [invalidate, liveBundle, onTerminalTransition, resource]);

  const isPolling =
    Boolean(requestId) &&
    !FINAL_STATUSES.has(liveBundle?.status as DataExportStatus);

  return {
    activeBundle: liveBundle,
    isPolling,
    isAdmin,
  };
};
